import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { DirectorySearchFilterRemoteDto, OrganisationEntryRemoteDto } from '../../../../../generated/swagger';
import { DirectoryColumn } from '../../../../samwebDescription/samweb/directory/directory-column';
import { DirectoryStructureState } from '../directory-structure/directory-structure.state';
import { DirectoryOrganisationDataState, DirectoryOrganisationDataStateModel } from './directory-organisation-data.state';

export class DirectoryOrganisationDataSelectors {
   public static NO_RESTRICTION_ENTRY = <OrganisationEntryRemoteDto>{
      id: -1,
      orgnam: '-',
   };

   @Selector([DirectoryOrganisationDataState, DirectoryStructureState.getParentStructureForStructureNumber])
   public static getOrganisationEntriesThatAreParentsOfEntry(
      state: DirectoryOrganisationDataStateModel,
      getParentStructureForStructureNumber: (organisationUnitNumber: string) => string
   ) {
      const recursiveFn = (
         targetOrganisationUnitId: string,
         sourceOrganisationUnitId: string,
         sourceEntry: OrganisationEntryRemoteDto
      ): OrganisationEntryRemoteDto[] => {
         const parentStructureId = getParentStructureForStructureNumber(sourceOrganisationUnitId);
         if (_.isNil(parentStructureId) || parentStructureId === '00') {
            return [sourceEntry];
         }
         const foundChildren = state[`organisation${parentStructureId}Entries`].filter(
            (x: OrganisationEntryRemoteDto) => x[`org${sourceOrganisationUnitId}`]?.value === sourceEntry.id
         );
         if (parentStructureId === targetOrganisationUnitId) {
            return foundChildren;
         }
         return _.flatMap(foundChildren, (x) => recursiveFn(targetOrganisationUnitId, parentStructureId, x));
      };
      return recursiveFn;
   }

   @Selector([
      DirectoryOrganisationDataState,
      DirectoryStructureState.getParentStructureForStructureNumber,
      DirectoryOrganisationDataSelectors.getOrganisationEntriesThatAreParentsOfEntry,
   ])
   public static directoryOrganisationFilterBySearchValue(
      state: DirectoryOrganisationDataStateModel,
      getParentStructureForStructureNumber: (organisationUnitNumber: string) => string,
      getOrganisationEntriesThatAreParentsOfEntry: (
         targetOrganisationUnitId: string,
         sourceOrganisationUnitId: string,
         sourceEntry: OrganisationEntryRemoteDto
      ) => OrganisationEntryRemoteDto[]
   ): (searchValue: string, searchInColumns: DirectoryColumn[]) => DirectorySearchFilterRemoteDto[] {
      return (searchValues: string, searchInColumns: DirectoryColumn[]) => {
         const result: DirectorySearchFilterRemoteDto[] = _.split(searchValues, ' ')
            .filter((x) => x.length > 0)
            .map((searchValue) => {
               const searchValueFoundIn = _.range(1, 21)
                  .map((x) => _.padStart(x.toString(), 2, '0'))
                  .map((range) => {
                     return {
                        organisationUnitId: range,
                        values: state[`organisation${range}Entries`].filter((x) =>
                           this.isOrganisationEntryMatchingSearch(
                              x,
                              searchValue,
                              searchInColumns.filter((searchColumn) => searchColumn.organisationUnitNumber === range)
                           )
                        ),
                     };
                  });
               const resolvedFoundEntries = _.flatMap(searchValueFoundIn, (foundInWrapper) => {
                  const valueFoundInResolvedEntries = foundInWrapper.values.map((x) => {
                     let resolvedOrganisationId = foundInWrapper.organisationUnitId;
                     while (true) {
                        const nextOrganisation = getParentStructureForStructureNumber(resolvedOrganisationId);
                        if (nextOrganisation === '00' || _.isNil(nextOrganisation)) {
                           break;
                        }
                        resolvedOrganisationId = nextOrganisation;
                     }
                     const resolvedOrganisationEntries = getOrganisationEntriesThatAreParentsOfEntry(
                        resolvedOrganisationId,
                        foundInWrapper.organisationUnitId,
                        x
                     );
                     return { resolvedOrganisationId, resolvedOrganisationEntryIds: resolvedOrganisationEntries.map((y) => y.id) };
                  });
                  return valueFoundInResolvedEntries;
               });
               return <DirectorySearchFilterRemoteDto>{
                  searchValue: searchValue,
                  valueIsAvailableInStructure01Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '01').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure02Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '02').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure03Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '03').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure04Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '04').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure05Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '05').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure06Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '06').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure07Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '07').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure08Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '08').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure09Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '09').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure10Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '10').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure11Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '11').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure12Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '12').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure13Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '13').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure14Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '14').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure15Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '15').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure16Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '16').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure17Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '17').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure18Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '18').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure19Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '19').map((x) => x.resolvedOrganisationEntryIds)
                  ),
                  valueIsAvailableInStructure20Ids: _.flatMap(
                     resolvedFoundEntries.filter((x) => x.resolvedOrganisationId === '20').map((x) => x.resolvedOrganisationEntryIds)
                  ),
               };
            });
         return result;
      };
   }

   @Selector([DirectoryStructureState.directoryStructureId, DirectoryOrganisationDataSelectors.getDirectoryStructureDataForOrgFn])
   public static getDirectoryStructureData(
      directoryStructureId: string,
      getDirectoryStructureDataForOrgFn: (organisationUnitNumber: string) => OrganisationEntryRemoteDto[]
   ): OrganisationEntryRemoteDto[] {
      return getDirectoryStructureDataForOrgFn(directoryStructureId);
   }

   @Selector([DirectoryOrganisationDataState.getOrganisationEntries])
   public static getDirectoryStructureDataForOrgFn(
      getOrganisationEntries: (organisationUnitNumber: string) => OrganisationEntryRemoteDto[]
   ): (orgStructureId: string) => OrganisationEntryRemoteDto[] {
      return (orgStructureId: string) => {
         if (_.isEmpty(orgStructureId)) {
            return [];
         }
         return getOrganisationEntries(_.padStart(orgStructureId, 2, '0'));
      };
   }

   @Selector([DirectoryOrganisationDataState.getOrganisationEntries])
   public static getDirectoryStructureValue(
      getOrganisationEntries: (orgStructureId: string) => OrganisationEntryRemoteDto[]
   ): (orgStructureId: string, value: number) => OrganisationEntryRemoteDto {
      return (orgStructureId: string, value: number) => {
         if (_.isEmpty(orgStructureId)) {
            return DirectoryOrganisationDataSelectors.NO_RESTRICTION_ENTRY;
         }
         const result = getOrganisationEntries(_.padStart(orgStructureId, 2, '0')).find((entry) => entry.id === value);
         return result ?? DirectoryOrganisationDataSelectors.NO_RESTRICTION_ENTRY;
      };
   }

   @Selector([DirectoryOrganisationDataSelectors.getDirectoryStructureData])
   public static getDirectoryRestrictionEntries(directoryStructureData: OrganisationEntryRemoteDto[]): OrganisationEntryRemoteDto[] {
      return [DirectoryOrganisationDataSelectors.NO_RESTRICTION_ENTRY, ...directoryStructureData];
   }

   private static isOrganisationEntryMatchingSearch(
      organisationEntry: OrganisationEntryRemoteDto,
      searchValue: string,
      searchInColumns: DirectoryColumn[] = null
   ): boolean {
      const organisationEntryContainsEntryThatMatchesSearchValue = _(searchInColumns).some((searchColumn) => {
         const columnValue = searchColumn.columnTyp.valueForColumnInstance(organisationEntry);
         if (!columnValue.isValueSet) {
            return false;
         }
         return _.includes(columnValue.value.toString().toLowerCase(), searchValue.toLowerCase());
      });
      return organisationEntryContainsEntryThatMatchesSearchValue;
   }
}
